"use client"

import type React from "react"
import { Lock } from "lucide-react"
import { useRouter } from "next/navigation"
import { useSchoolAccess } from "../contexts/SchoolAccessContext"
import { analytics } from "../lib/firebase"
import { logEvent } from "firebase/analytics"

interface BlurredContentProps {
  children: React.ReactNode
  schoolId?: string | number
  schoolName?: string
}

export const BlurredContent: React.FC<BlurredContentProps> = ({ children, schoolId, schoolName }) => {
  const router = useRouter()
  const { hasAccess, isLoading } = useSchoolAccess()

  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 rounded-md min-h-[100px]"></div>
  }

  if (hasAccess) {
    return <>{children}</>
  }

  const handleClick = () => {
    // Extract the school ID if not provided
    let id = schoolId

    // If no schoolId is provided as prop, try to extract from URL
    if (!id) {
      const pathSegments = window.location.pathname.split("/")
      const lastSegment = pathSegments.pop() || ""

      // If we're on a school-specific page, the last segment will be the SEO slug
      // Extract just the numeric ID from the end of the SEO slug
      if (pathSegments.includes("skola") && pathSegments.includes("enhet")) {
        // The SEO slug format is name-id, so extract the ID from the end
        const matches = lastSegment.match(/-(\d+)$/)
        id = matches ? matches[1] : lastSegment
      } else {
        id = lastSegment
      }
    }

    // Make sure id is a string
    const idStr = id ? id.toString() : ""
    const name = schoolName || document.querySelector("h1")?.textContent || "denna skola"

    // Track the lock-click event before redirecting
    if (analytics) {
      logEvent(analytics, "click_lock_49kr", {
        school_id: idStr,
        school_name: name,
      })
    }

    // Always redirect to benefits page, regardless of login status
    router.push(`/benefits?school_id=${idStr}&school_name=${encodeURIComponent(name)}`)
  }

  return (
    <div className="relative">
      <div className="opacity-0 pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-0 bg-gray-200 opacity-50 blur-md"></div>
        <div
          className="relative z-10 text-gray-700 flex flex-col items-center p-4 text-center cursor-pointer hover:bg-gray-100 hover:bg-opacity-50 rounded-md transition-colors"
          onClick={handleClick}
        >
          <Lock className="w-6 h-6 mb-2" />
          <span className="text-sm font-medium">Klicka för att låsa upp</span>
        </div>
      </div>
    </div>
  )
}

